/**
 * Cliente da API do painel.
 *
 * Mesma origem sempre (proxy do Vite em desenvolvimento, nginx em producao),
 * entao o cookie de sessao HttpOnly viaja sozinho -- nada de token em
 * localStorage, que um XSS leria. O CSRF vai no cabecalho, vindo da sessao.
 */

const BASE = "/api/painel";

let csrf = "";
export function definirCSRF(valor: string): void {
  csrf = valor;
}

export class ErroAPI extends Error {
  constructor(
    readonly status: number,
    readonly codigo: string,
    mensagem: string,
    readonly campos?: Record<string, string>,
  ) {
    super(mensagem);
    this.name = "ErroAPI";
  }
}

async function pedir<T>(caminho: string, init: RequestInit = {}): Promise<T> {
  const metodo = (init.method ?? "GET").toUpperCase();
  const cabecalhos: Record<string, string> = {
    ...(init.body ? { "Content-Type": "application/json" } : {}),
    ...(metodo !== "GET" && csrf ? { "X-CSRF-Token": csrf } : {}),
    ...((init.headers as Record<string, string>) ?? {}),
  };

  const r = await fetch(BASE + caminho, {
    ...init,
    headers: cabecalhos,
    credentials: "same-origin",
  });

  if (r.status === 204) return undefined as T;

  let corpo: unknown = null;
  try {
    corpo = await r.json();
  } catch { /* resposta sem corpo */ }

  if (!r.ok) {
    const c = (corpo ?? {}) as { erro?: string; mensagem?: string; campos?: Record<string, string> };
    throw new ErroAPI(
      r.status,
      c.erro ?? "desconhecido",
      c.mensagem ?? `Falha na requisicao (${r.status}).`,
      c.campos,
    );
  }
  return corpo as T;
}

export const api = {
  get: <T>(c: string) => pedir<T>(c),
  post: <T>(c: string, corpo?: unknown) =>
    pedir<T>(c, { method: "POST", body: corpo === undefined ? undefined : JSON.stringify(corpo) }),
  put: <T>(c: string, corpo?: unknown) =>
    pedir<T>(c, { method: "PUT", body: corpo === undefined ? undefined : JSON.stringify(corpo) }),
};

// ------------------------------------------------------------- formatacao

const p2 = (n: number) => String(n).padStart(2, "0");

export function data(v: string | Date | null | undefined): string {
  if (!v) return "--";
  const d = new Date(v);
  return `${p2(d.getDate())}/${p2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export function dataHora(v: string | Date | null | undefined): string {
  if (!v) return "--";
  const d = new Date(v);
  return `${data(d)} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

const plural = (n: number, s: string, p: string) => (n === 1 ? s : p);

/** Escreve a duracao como o suporte fala ao telefone. */
export function desde(v: string | Date | null | undefined): string {
  if (!v) return "nunca";
  const ms = Date.now() - new Date(v).getTime();
  if (ms < 60_000) return "agora ha pouco";
  if (ms < 3600_000) {
    const n = Math.floor(ms / 60_000);
    return `ha ${n} ${plural(n, "minuto", "minutos")}`;
  }
  if (ms < 48 * 3600_000) {
    const n = Math.floor(ms / 3600_000);
    return `ha ${n} ${plural(n, "hora", "horas")}`;
  }
  const n = Math.floor(ms / 86_400_000);
  if (n < 60) return `ha ${n} dias`;
  const meses = Math.floor(n / 30);
  return `ha ${meses} ${plural(meses, "mes", "meses")}`;
}

export function horasEmProsa(horas: number): string {
  if (horas < 48) return `${horas} ${plural(horas, "hora", "horas")}`;
  const d = Math.floor(horas / 24);
  return `${d} ${plural(d, "dia", "dias")}`;
}

/** Uptime legivel. Segundos crus na tela sao ilegiveis. */
export function uptime(segundos: number | null | undefined): string {
  if (!segundos) return "--";
  const d = Math.floor(segundos / 86400);
  const h = Math.floor((segundos % 86400) / 3600);
  if (d > 0) return `${d} ${plural(d, "dia", "dias")} ${h} h`;
  const m = Math.floor((segundos % 3600) / 60);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}

export function gb(mb: number | null | undefined): string {
  if (!mb) return "--";
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
}
